import { useEffect, useMemo, useState } from "react";
import { ArrowLeftRight, FileSpreadsheet, CheckCircle2, AlertTriangle, XCircle, Loader2, Download, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CAT_STYLE = {
  VALID: "bg-emerald-100 text-emerald-700",
  INVALID_DATE: "bg-amber-100 text-amber-700",
  INVALID_SERIAL: "bg-red-100 text-red-700",
  UNKNOWN_TYPE: "bg-red-100 text-red-700",
  NOT_FOUND_OR_INELIGIBLE: "bg-red-100 text-red-700",
  ALREADY_IN_DESTINATION: "bg-red-100 text-red-700",
  AMBIGUOUS_MULTIPLE: "bg-red-100 text-red-700",
  DUPLICATE_IN_FILE: "bg-red-100 text-red-700",
};
const CAT_RANK = { INVALID_SERIAL: 0, UNKNOWN_TYPE: 0, NOT_FOUND_OR_INELIGIBLE: 0, ALREADY_IN_DESTINATION: 0, AMBIGUOUS_MULTIPLE: 0, DUPLICATE_IN_FILE: 0, INVALID_DATE: 1, VALID: 2 };
const CAT_LABELS = {
  VALID: "Valid (transferable)",
  INVALID_DATE: "Invalid / unparsed date",
  INVALID_SERIAL: "Blank material number",
  UNKNOWN_TYPE: "Unknown material type",
  NOT_FOUND_OR_INELIGIBLE: "Not found / not eligible",
  ALREADY_IN_DESTINATION: "Already in destination",
  AMBIGUOUS_MULTIPLE: "Ambiguous (multiple matches)",
  DUPLICATE_IN_FILE: "Duplicate in file",
};

export default function MaterialTransfer() {
  const { ctx, options } = useWorkspace();
  const [projectId, setProjectId] = useState(ctx.project?.id || "");
  const [sourceId, setSourceId] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [catFilter, setCatFilter] = useState("__all__");

  useEffect(() => { setProjectId(ctx.project?.id || ""); setSourceId(""); setConfirmed(false); }, [ctx.project?.id]);

  const projects = useMemo(() => options.projects || [], [options.projects]);
  const projName = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const srcName = useMemo(() => projects.find((p) => p.id === sourceId), [projects, sourceId]);

  const changeProject = (v) => { setProjectId(v); setConfirmed(false); setPreview(null); };

  const downloadTemplate = async () => {
    setDlBusy(true);
    try {
      const res = await api.get("/materials/transfer/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "material_transfer_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
    finally { setDlBusy(false); }
  };

  const doPreview = async () => {
    if (!projectId || !file) { toast.error("Confirm destination project and choose a file"); return; }
    setLoading(true); setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("project_id", projectId);
      if (sourceId) fd.append("source_project_id", sourceId);
      const { data } = await api.post("/materials/transfer/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.already_applied) toast.info("This exact file was already applied for this destination");
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };

  const doApply = async () => {
    if (!preview?.batch_id) return;
    setApplying(true);
    try {
      const { data } = await api.post("/materials/transfer/apply", { batch_id: preview.batch_id });
      toast.success(`Transferred: ${data.transferred} moved${data.failed ? `, ${data.failed} failed` : ""}`);
      setPreview((p) => ({ ...p, already_applied: true, result: data }));
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setCatFilter("__all__"); };

  const downloadReport = async () => {
    if (!preview?.batch_id) return;
    try {
      const res = await api.get(`/materials/transfer/${preview.batch_id}/report`, {
        params: catFilter !== "__all__" ? { category: catFilter } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `material_transfer_validation_${preview.batch_id.slice(0, 8)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Report download failed"); }
  };

  const rows = useMemo(() => {
    if (!preview?.rows) return [];
    return catFilter === "__all__" ? preview.rows : preview.rows.filter((r) => r.category === catFilter);
  }, [preview, catFilter]);

  const s = preview?.summary || {};
  const canApply = preview && !preview.already_applied && (s.applicable || 0) > 0;

  return (
    <div>
      <PageHeader title="Project → Project Material Transfer" subtitle="Pick destination project → Download template → Upload → Preview → Apply. Transferred stock lands as GRN Pending for the destination store." />

      {!confirmed ? (
        <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="transfer-context-step">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-blue-600" /> Confirm Destination Project</div>
          <p className="mb-4 text-xs text-slate-500">Materials listed in your file will be pulled from the <b>source</b> project INTO this <b>destination</b> project and set to GRN Pending.</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>From Project (source) *</Label>
              <SearchableSelect testid="transfer-source-select" value={sourceId} onChange={(v) => { setSourceId(v); setConfirmed(false); setPreview(null); }} options={projects.filter((p) => p.id !== projectId).map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name, sub: p.client || p.location }))} placeholder="Select source project" className="mt-1" />
            </div>
            <div>
              <Label>To Project (destination) *</Label>
              <SearchableSelect testid="transfer-project-select" value={projectId} onChange={changeProject} options={projects.filter((p) => p.id !== sourceId).map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name, sub: p.client || p.location }))} placeholder="Select destination project" className="mt-1" />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <Button data-testid="transfer-confirm-context-btn" disabled={!projectId || !sourceId || projectId === sourceId} onClick={() => setConfirmed(true)} className="bg-blue-600 hover:bg-blue-700">
              Confirm &amp; Continue <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5" data-testid="transfer-confirmed-bar">
            <div className="text-sm text-slate-700">
              <span className="text-slate-400">Transfer:</span> <span className="font-semibold text-slate-700">{srcName?.project_name}</span> <ArrowRight className="mx-1 inline h-3.5 w-3.5 text-slate-400" /> <span className="font-semibold text-blue-800">{projName?.project_name}</span>
            </div>
            <Button variant="outline" size="sm" data-testid="transfer-back-context-btn" onClick={() => { setConfirmed(false); reset(); }}><ArrowLeft className="mr-1.5 h-4 w-4" /> Change Projects</Button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium text-slate-600">Upload the Transfer template (Material Type · Material Number · Date of Transfer)</span>
              <Button variant="outline" size="sm" data-testid="transfer-template-download" onClick={downloadTemplate} disabled={dlBusy}>
                {dlBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download Template
              </Button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 hover:bg-slate-50" data-testid="transfer-file-label">
                <FileSpreadsheet className="h-5 w-5 text-blue-600" />
                <span className="text-sm text-slate-600">{file ? file.name : "Choose .xlsx file"}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="transfer-file-input" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
              </label>
              <Button data-testid="transfer-preview-btn" onClick={doPreview} disabled={loading || !file} className="bg-slate-800 hover:bg-slate-900">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ArrowLeftRight className="mr-1.5 h-4 w-4" /> Preview</>}
              </Button>
            </div>
          </div>

          {preview && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="transfer-summary">
                <SummaryCard label="Total" value={s.total} tone="slate" />
                <SummaryCard label="Valid" value={s.valid} tone="green" icon={CheckCircle2} />
                <SummaryCard label="Warnings" value={s.warning} tone="amber" icon={AlertTriangle} />
                <SummaryCard label="Errors" value={s.error} tone="red" icon={XCircle} />
                <SummaryCard label="Applicable" value={s.applicable} tone="blue" />
              </div>

              {Object.keys(preview.categories || {}).length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="transfer-cat-breakdown">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Breakdown by result</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.categories)
                      .sort((a, b) => (CAT_RANK[a[0]] ?? 3) - (CAT_RANK[b[0]] ?? 3) || b[1] - a[1])
                      .map(([cat, count]) => (
                        <button key={cat} type="button" onClick={() => setCatFilter((prev) => (prev === cat ? "__all__" : cat))}
                          data-testid={`transfer-cat-chip-${cat}`}
                          className={`rounded-full border px-3 py-1 text-xs transition ${CAT_STYLE[cat] || "bg-slate-100 text-slate-600"} ${catFilter === cat ? "ring-2 ring-offset-1 ring-slate-400" : "hover:opacity-90"}`}>
                          <span className="font-bold">{count}</span> × {CAT_LABELS[cat] || cat}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Select value={catFilter} onValueChange={setCatFilter}>
                  <SelectTrigger data-testid="transfer-cat-filter" className="w-64"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All categories</SelectItem>
                    {Object.keys(preview.categories || {}).map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c] || c} ({preview.categories[c]})</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadReport} data-testid="transfer-download-report"><Download className="mr-1.5 h-4 w-4" /> Download Report</Button>
                  {preview.already_applied ? (
                    <Badge className="bg-emerald-100 px-3 py-1.5 text-emerald-700">Applied · {preview.result?.transferred ?? 0} transferred</Badge>
                  ) : (
                    <>
                      <Button variant="outline" onClick={reset} data-testid="transfer-reset-btn">Reset</Button>
                      <Button data-testid="transfer-apply-btn" onClick={doApply} disabled={!canApply || applying} className="bg-blue-600 hover:bg-blue-700">
                        {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Transfer ${s.applicable || 0} materials`}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100/80">
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Row</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Material No.</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Transfer Date</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Source</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Result</th>
                    </tr>
                  </thead>
                  <tbody data-testid="transfer-rows">
                    {rows.slice(0, 500).map((r) => (
                      <tr key={r.row_no} className="border-b border-slate-100">
                        <td className="px-4 py-2 text-sm text-slate-500">{r.row_no}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.serial || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.material_type}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.transfer_date || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.source_project || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2">
                          <Badge className={`${CAT_STYLE[r.category] || "bg-slate-100 text-slate-600"} hover:opacity-100`}>{CAT_LABELS[r.category] || r.category}</Badge>
                          {r.message && <span className="ml-2 text-xs text-slate-400">{r.message}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, icon: Icon }) {
  const tones = { slate: "text-slate-700", green: "text-emerald-600", amber: "text-amber-600", red: "text-red-600", blue: "text-blue-600" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tones[tone]}`}>{value ?? 0}</div>
    </div>
  );
}
